import { useEffect, useRef, useState } from 'react'

const NO_PAGINATION_ROWS = 1000000
const FILTER_TOGGLE_EVENT = 'layout:filters-toggle'

export default function useResponsivePageSize({
  reservedHeight = 420,
  footerHeight = 76,
  rowHeight = 52,
  minRows = 5,
  maxRows = 16
} = {}) {
  const containerRef = useRef(null)

  const getAvailableHeight = () => {
    if (typeof window === 'undefined') return rowHeight * minRows

    const containerTop = containerRef.current?.getBoundingClientRect?.().top
    if (Number.isFinite(containerTop)) {
      return window.innerHeight - containerTop - footerHeight
    }

    return window.innerHeight - reservedHeight
  }

  const applyScrollableHeight = () => {
    const container = containerRef.current
    if (!container) return

    const availableHeight = Math.max(Math.floor(getAvailableHeight()), rowHeight * 2)
    const contentHeight = container.scrollHeight

    if (contentHeight > availableHeight) {
      container.style.maxHeight = `${availableHeight}px`
    } else {
      container.style.maxHeight = 'none'
    }
  }

  const [rowsPerPage, setRowsPerPage] = useState(NO_PAGINATION_ROWS)

  useEffect(() => {
    const updateRowsPerPage = () => {
      applyScrollableHeight()
      setRowsPerPage((prev) => {
        // Keep backward compatibility with page components that still read rowsPerPage,
        // while effectively disabling pagination in favor of scrollable tables.
        const next = NO_PAGINATION_ROWS
        return prev === next ? prev : next
      })
    }

    const handleLayoutChange = () => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(updateRowsPerPage)
      })
    }

    updateRowsPerPage()
    window.addEventListener('resize', handleLayoutChange)
    window.addEventListener(FILTER_TOGGLE_EVENT, handleLayoutChange)

    let resizeObserver = null
    let parentResizeObserver = null
    let contentResizeObserver = null
    let mutationObserver = null
    if (typeof ResizeObserver !== 'undefined' && containerRef.current) {
      resizeObserver = new ResizeObserver(handleLayoutChange)
      resizeObserver.observe(containerRef.current)

      if (containerRef.current.parentElement) {
        parentResizeObserver = new ResizeObserver(handleLayoutChange)
        parentResizeObserver.observe(containerRef.current.parentElement)
      }

      if (containerRef.current.firstElementChild) {
        contentResizeObserver = new ResizeObserver(handleLayoutChange)
        contentResizeObserver.observe(containerRef.current.firstElementChild)
      }
    }

    if (typeof MutationObserver !== 'undefined' && containerRef.current) {
      mutationObserver = new MutationObserver(handleLayoutChange)
      mutationObserver.observe(containerRef.current, {
        childList: true,
        subtree: true
      })
    }

    return () => {
      window.removeEventListener('resize', handleLayoutChange)
      window.removeEventListener(FILTER_TOGGLE_EVENT, handleLayoutChange)
      resizeObserver?.disconnect()
      parentResizeObserver?.disconnect()
      contentResizeObserver?.disconnect()
      mutationObserver?.disconnect()
    }
  }, [reservedHeight, footerHeight, rowHeight, minRows, maxRows])

  return { containerRef, rowsPerPage }
}